import { Col, Grid } from '@koaly/grid';
import { Card, Container, ContentMedia, Hero, Picture,Tag, TitleContent } from '@koaly/koaly-ui';
import { NextPage } from 'next';
import Head from 'next/head';
import React from 'react';

import {
  heroImage,
} from '../assets';
import { Footer } from '../components/Footer/Footer';
import { FooterCTAWaitingList } from '../components/FooterCTAWaitingList/FooterCTAWaitingList';
import { TopBar } from '../components/TopBar/TopBar';
import { responsiveToSource } from '../helpers/responsiveToSources';

export const Functionaliteiten: NextPage = () => {
  return (
    <React.Fragment>
      <Head>
        <title>Functionaliteiten die patiënt en praktijk bedienen - Koaly</title>
        <meta
          name="description"
          content="Koaly biedt moderne praktijkwebsites. Zo ben je als huisarts minder tijd kwijt aan websitebeheer en administratie, terwijl de service voor patiënten optimaal is."
        />
        <meta
          name="og:description"
          content="Koaly biedt moderne praktijkwebsites. Zo ben je als huisarts minder tijd kwijt aan websitebeheer en administratie, terwijl de service voor patiënten optimaal is."
        />

      </Head>
      <Container background={true} fadeOut={true} backgroundFigures={true} variant="secondary">
        <TopBar variant="secondary" />
        <Hero
          variant="secondary"
          title="Functionaliteiten die patiënt en praktijk bedienen"
          description="Koaly biedt moderne praktijkwebsites. Zo ben je als huisarts of praktijkmanager minder tijd kwijt aan websitebeheer en administratie, terwijl de service voor patiënten optimaal is."
          media={
            <Picture
              {...responsiveToSource(heroImage)}
              lazyLoading={true}
              alt="Koaly praktijkwebsite op een beeldscherm"
            />
          }
        />
      </Container>
      <Container>
        <ContentMedia
          spacing={true}
          title="Eenvoudig in beheer"
          description="De website staat klaar en is eenvoudig te personaliseren. Kies eigen kleuren via het kleurenpalet en vul de medewerkerspagina aan met tekst en beeld. Ook acute veranderingen zoals gewijzigde openingstijden en/of spoedberichten zijn zo geplaatst."
          mediaPosition="left"
          mediaFit="contain"
          media={
            <img
              loading="lazy"
              src="/assets/illustrations/herkenbaarheid.svg"
              alt="Illustratie met kleurenpaletten en logo upload knop"
            />
          }
        />
        <ContentMedia
          spacing={true}
          title="Toegankelijk en inclusief"
          description="De praktijkwebsite is bereikbaar via desktop en mobiele apparaten. Extra navigatiemogelijkheden maken de website toegankelijk voor patiënten met een (tijdelijke) beperking. Denk hierbij aan het kunnen ‘tabben’ door de website, of een optimaal gebruik van screenreaders voor slechtzienden."
          mediaFit="contain"
          media={
            <img
              loading="lazy"
              src="/assets/illustrations/product.svg"
              alt="Illustratie van het product"
            />
          }
        />
        <ContentMedia
          title="Afspraken inplannen"
          mediaPosition="left"
          description="Patiënten maken gemakkelijk een afspraak via de website. Het is dan niet meer nodig om met de praktijk te bellen of te mailen. Het is mogelijk om deze optie (tijdelijk) uit te schakelen, wanneer dat jouw praktijk beter uitkomt."
          mediaFit="contain"
          media={
            <img
              loading="lazy"
              src="/assets/illustrations/features-afspraakinplannen.svg"
              alt="Illustratie van een formulier om een afspraak in te plannen"
            />
          }
        />
        <ContentMedia
          spacing={true}
          title="Digitaal inschrijfformulier"
          description="Via het digitale inschrijfformulier kunnen potentiële patiënten zich direct inschrijven. Wanneer er een wachtlijst is, kan een bezoeker contactgegevens achterlaten."
          mediaFit="contain"
          media={
            <img
              loading="lazy"
              src="/assets/illustrations/features-inschrijfformulier.svg"
              alt="Illustratie van een formulier om je eigen aan te melden bij een praktijk"
            />
          }
        />
        <ContentMedia
          spacing={true}
          title="Herhaalrecepten"
          mediaPosition="left"
          description="Patiënten vragen herhaalrecepten gemakkelijk aan via een beveiligd formulier. Na jouw goedkeuring krijgen ze automatisch een bericht wanneer hun medicijn klaarligt bij de apotheek."
          mediaFit="contain"
          media={
            <img
              loading="lazy"
              src="/assets/illustrations/features-herhaalrecept.svg"
              alt="Illustratie over het proces om een herhaalrecept goed te keuren"
            />
          }
        />
        <ContentMedia
          spacing={true}
          title="Beveiligd en privacy proof"
          mediaPosition="right"
          description="Communicatie tussen patiënt en praktijk is altijd beveiligd en de praktijkwebsite voldoet aan de privacywet (denk aan AVG). Wij houden wetswijzigingen nauwlettend in de gaten en informeren wanneer er iets voor jou verandert."
          mediaFit="contain"
          media={
            <img
              loading="lazy"
              src="/assets/illustrations/beveiligd.svg"
              alt="Illustratie over veiligheid, ssl, encrypted etc"
            />
          }
        />
        <ContentMedia
          spacing={true}
          title="Realtime informatie"
          mediaPosition="left"
          mediaFit="contain"
          description="De praktijkwebsite laat zien welke medewerkers aanwezig zijn. Is de praktijk dicht? Dan geeft de website de contactgegevens van de huisartsenpost weer."
          media={
            <img
              loading="lazy"
              src="/assets/illustrations/features-realtime.svg"
              alt="Illustratie over realtime telefoonnummer / openingstijden"
            />
          }
        />
        <TitleContent
          title="Toekomstige functionaliteiten"
          spacing={true}
          align="center"
          id="roadmap"
          titleSpaceAfter={32}
        >
          <Grid gutter={10}>
            <Col width={{ xs: 1, s: 1 / 2 }}>
              <TitleContent
                title="Op de planning"
                titleSize="h3"
                titleSpaceAfter={4}
                description="Wij werken aan de volgende functionaliteiten:"
                align="center"
              >
                <Card title="E-consult" spaceAfter={10} right={<Tag>Q1 2022</Tag>}>
                  E-consults via de eigen praktijkwebsite. Op deze manier hoeven patiënten niet doorverwezen te worden
                  naar diensten van derden voor een e-consult.
                </Card>
                <Card title="Redactionele content" spaceAfter={10} right={<Tag>Q1 2022</Tag>}>
                  Jouw praktijk kan gebruikmaken van actuele content rondom medisch nieuws. Denk aan informatie en
                  veiligheidsreglementen rondom het coronavirus. De content is handmatig aan te passen naar eigen stijl.
                </Card>
                <Card title="Ondersteuning groepspraktijken" spaceAfter={10} right={<Tag>Q1 2022</Tag>}>
                  Mogelijkheid om meerdere praktijken onder te brengen binnen dezelfde website.
                </Card>
                <Card title="Thuisarts.nl integratie" spaceAfter={10} right={<Tag>Q1 2022</Tag>}>
                  Met een thuisarts.nl integratie kunnen patiënten informatie van Thuisarts raadplegen via jouw eigen
                  website.
                </Card>
                <Card title="Behandelingen overzicht" spaceAfter={10} right={<Tag>Q2 2022</Tag>}>
                  Creëer een overzicht met de meestvoorkomende behandelingen om patiënten van informatie te voorzien.
                </Card>
              </TitleContent>
            </Col>
            <Col width={{ xs: 1, s: 1 / 2 }}>
              <TitleContent
                title="In overweging"
                titleSize="h3"
                titleSpaceAfter={4}
                description="Wij onderzoeken de interesse in de volgende functionaliteiten:"
                align="center"
              >
                <Card title="Contentproductie" spaceAfter={10} right={<Tag>Q2 2022</Tag>}>
                  Ons eigen productieteam maakt foto’s en teksten die jouw praktijkwebsite aankleden en persoonlijk
                  maken.
                </Card>
                <Card title="Online chat" spaceAfter={10} right={<Tag>Q3 2022</Tag>}>
                  Een externe, professionele service behandelt de vragen van jouw patiënten via jouw eigen website.
                </Card>
                <Card title="Terugbelfunctie via callcenter" spaceAfter={10} right={<Tag>Q3 2022</Tag>}>
                  Wanneer jouw praktijk even niet bereikbaar is, nemen de tot doktersassistenten opgeleide werknemers
                  van het callcenter het over. Zij kunnen bijvoorbeeld afspraken inplannen en bloeduitslagen van de
                  patiënt uitlezen.
                </Card>
              </TitleContent>
            </Col>
          </Grid>
        </TitleContent>
      </Container>
      <Container background={true} variant="secondary">
        <FooterCTAWaitingList />
      </Container>
      <Container>
        <Footer />
      </Container>
    </React.Fragment>
  );
};

export default Functionaliteiten;
